import { format as formatDate, parseISO } from "date-fns";
import {
  CLASSES_EDUCATIONAL_FEE,
  MEDICAL_INSURANCE,
  SPAIN_MEDICAL_INSURANCE,
} from "@/components/program-manager/sessions/constants";
import { buildStatusRow } from "@/components/StudentFulfillment/components/modals/utils";
import { REQUESTED_ROOM_TYPE, TIERS_OBJ } from "@/constants";
import { formatCalendarDate } from "@/util/calendarDate";
import { formattedPriceFromCents } from "@/util/formatter";
import { EXCURSION_INCLUDED_TEXTS } from "../constants";

/** Date style for the excursion rows of the order details table. */
const OCCURRENCE_DATE_FORMAT = "MM/dd/yyyy";

const getPriceOverrideStatusForRow = (lineItem) => {
  if (lineItem) {
    const normalized = {
      latest_approved_price_override: lineItem?.price_override ?? lineItem?.latest_approved_price_override,
      latest_unapproved_price_override: lineItem?.latest_unapproved_price_override,
    };
    const status = buildStatusRow(normalized);

    return status?.display ? status : undefined;
  }
};

const buildSessionMoreInfo = (orderPaymentProcessing, orderData) => {
  return {
    country: `${orderPaymentProcessing.value?.order_metadata?.session_country ?? ""}`,
    terms: orderPaymentProcessing.value?.order_metadata?.terms ?? "",
    date: `${formatDate(parseISO(orderPaymentProcessing.value?.order_metadata?.start_date), "MM/dd/yyyy") ?? ""} - ${
      formatDate(parseISO(orderPaymentProcessing.value?.order_metadata?.end_date), "MM/dd/yyyy") ?? ""
    }`,
    credits: orderData?.value?.order?.credits ? `${orderData.value.order.credits} Credits` : "",
  };
};

const upgradeDiscountPriceCalculation = ({ totalPrice = 0, basePrice = 0 }) => {
  const priceDiff = totalPrice - basePrice;

  if (priceDiff === 0) {
    return "$0";
  }

  const priceAmmount = formattedPriceFromCents(Math.abs(priceDiff));
  const positiveOrNegative = priceDiff >= 0 ? "+" : "-";
  return `${positiveOrNegative} ${priceAmmount}`;
};

const buildOccurrenceMoreInfo = (eventObject, isDefault) => {
  let date = "";

  let excursionIncluded = EXCURSION_INCLUDED_TEXTS?.notAvailable;
  if (isDefault === true) {
    excursionIncluded = EXCURSION_INCLUDED_TEXTS?.included;
  } else if (isDefault === false) {
    excursionIncluded = EXCURSION_INCLUDED_TEXTS?.addon;
  }

  const location =
    eventObject?.location_city && eventObject?.location_country
      ? `${eventObject?.location_city}, ${eventObject?.location_country}`
      : "";

  if (eventObject?.start_date) {
    date = formatCalendarDate(eventObject.start_date, OCCURRENCE_DATE_FORMAT);
    if (eventObject?.end_date) {
      date = `${date} - ${formatCalendarDate(eventObject.end_date, OCCURRENCE_DATE_FORMAT)}`;
    }
  }

  return {
    date: date,
    location: location,
    duration: `${eventObject?.event_duration} ${eventObject?.event_duration_unit}`,
    excursionIncluded: excursionIncluded,
  };
};

const getTableRowData = (
  id,
  type,
  name,
  company,
  info,
  studentPays,
  institutionPays,
  totalPrice,
  upgradeDiscountPrice,
  status,
  created_updated_date,
  priceOverrideStatus,
) => ({
  id: id,
  Type: type,
  Name: name,
  "Company/Vendor": company,
  "More Info": info,
  "Student Pays": studentPays,
  "Institution Pays": institutionPays,
  "Total Price": totalPrice,
  "Upgrade/Discont": upgradeDiscountPrice,
  Status: status,
  "Last Time Updated": created_updated_date ? formatDate(parseISO(created_updated_date), "MM/dd/yyyy") : "",
  ...(priceOverrideStatus && {
    priceOverrideStatus: {
      display: priceOverrideStatus.display,
      labelStatus: priceOverrideStatus.labelStatus,
      labelColor: priceOverrideStatus.labelColor,
      icon: priceOverrideStatus.icon,
      displayRevert: priceOverrideStatus.displayRevert,
      revertLabel: priceOverrideStatus.revertLabel,
    },
  }),
});

const getSessionTableRow = (orderPaymentProcessing, orderData, programSession) =>
  getTableRowData(
    programSession?.items?.id ?? "",
    `Academic: ${orderPaymentProcessing.value?.order_metadata?.program_type ?? ""} Session`,
    {
      name: orderPaymentProcessing.value?.order_metadata?.program_name ?? "",
      subtitle: `(SF Session ID: ${programSession?.items?.salesforce_id || ""})`,
    },
    "",
    buildSessionMoreInfo(orderPaymentProcessing, orderData),
    `${formattedPriceFromCents(orderData?.value?.order?.student_total_price_in_cents)}`,
    `${formattedPriceFromCents(orderData?.value?.order?.institution_total_price_in_cents)}`,
    `${formattedPriceFromCents(orderData?.value?.order?.total_price_in_cents)}`,
    "",
    "",
    programSession?.items?.updated_at ?? programSession?.items?.created_at ?? "",
  ) ?? {};

const getHousingTableRow = (housing, roomObject, orderRoomHousingId) => {
  const priceOverrideStatus = getPriceOverrideStatusForRow(roomObject);

  return (
    getTableRowData(
      housing?.id ?? "",
      "Housing",
      { name: housing?.name ?? "" },
      housing?.vendor?.name ?? "",
      {
        housing_info: orderRoomHousingId.value
          ? [
              housing?.type,
              TIERS_OBJ[housing?.tier?.name],
              REQUESTED_ROOM_TYPE[roomObject?.room?.type],
              housing?.subcategory,
            ]
              .filter(Boolean)
              .join(" / ")
          : "",
      },
      orderRoomHousingId.value ? `${formattedPriceFromCents(roomObject?.student_total_price_in_cents)}` : "",
      orderRoomHousingId.value ? `${formattedPriceFromCents(roomObject?.institution_total_price_in_cents)}` : "",
      orderRoomHousingId.value ? `${formattedPriceFromCents(roomObject?.total_price_in_cents)}` : "",
      upgradeDiscountPriceCalculation({
        totalPrice: roomObject?.total_price_in_cents ?? 0,
        basePrice: roomObject?.base_price_in_cents ?? 0,
      }),
      roomObject?.status ?? "",
      roomObject?.updated_at ?? roomObject?.created_at ?? "",
      priceOverrideStatus,
    ) ?? {}
  );
};

const getInsuranceTableRow = (orderData) => {
  const insuranceItem = orderData?.value?.product_details?.misc_products?.find(
    (misc) => misc?.misc_product?.id === MEDICAL_INSURANCE.id || misc?.misc_product?.id === SPAIN_MEDICAL_INSURANCE.id,
  );
  const priceOverrideStatus = getPriceOverrideStatusForRow(insuranceItem);
  return insuranceItem
    ? getTableRowData(
        insuranceItem?.misc_product?.id,
        "Insurance",
        { name: insuranceItem?.misc_product?.name ?? "" },
        "",
        "",
        `${formattedPriceFromCents(insuranceItem?.student_total_price_in_cents)}`,
        `${formattedPriceFromCents(insuranceItem?.institution_total_price_in_cents)}`,
        `${formattedPriceFromCents(insuranceItem?.total_price_in_cents)}`,
        "",
        insuranceItem?.status ?? "",
        insuranceItem?.updated_at ?? insuranceItem?.created_at ?? "",
        priceOverrideStatus,
      )
    : {};
};

const getInternshipTableRows = (orderData, internshipDataMap) =>
  orderData?.value?.product_details?.internships.map((lineItem) => {
    const {
      internship = {},
      student_total_price_in_cents,
      institution_total_price_in_cents,
      total_price_in_cents,
      status,
      updated_at,
      created_at,
    } = lineItem;
    const {
      company = "",
      street_address_1 = "",
      street_address_2 = "",
      city = "",
      country = "",
      postal_code = "",
    } = internshipDataMap?.[internship?.id] ?? {};
    const priceOverrideStatus = getPriceOverrideStatusForRow(lineItem);
    return getTableRowData(
      internship?.id ?? "",
      `Academic: Internships: ${orderData?.value?.product_details?.internships?.length}`,
      internship?.title ?? "",
      company,
      {
        street_address_1,
        street_address_2,
        city,
        country,
        postal_code,
      },
      `${formattedPriceFromCents(student_total_price_in_cents)}`,
      `${formattedPriceFromCents(institution_total_price_in_cents)}`,
      `${formattedPriceFromCents(total_price_in_cents)}`,
      "",
      status ?? "",
      updated_at ?? created_at ?? "",
      priceOverrideStatus,
    );
  }) ?? [];

const getOccurrencesTableRows = (orderData, eventsDatesMap) =>
  orderData?.value?.product_details?.event_occurrences?.map((event) => {
    const priceOverrideStatus = getPriceOverrideStatusForRow(event);
    return getTableRowData(
      event?.event_occurrence?.event_id ?? "",
      `Academic: Excursions: ${orderData?.value?.product_details?.event_occurrences?.length}`,
      event?.event_occurrence?.event_title ?? "",
      "",
      buildOccurrenceMoreInfo(eventsDatesMap?.[event?.event_occurrence?.id], event?.is_default),
      `${formattedPriceFromCents(event?.student_total_price_in_cents)}`,
      `${formattedPriceFromCents(event?.institution_total_price_in_cents)}`,
      `${formattedPriceFromCents(event?.total_price_in_cents)}`,
      "",
      event?.status ?? "",
      event?.updated_at ?? event?.created_at ?? "",
      priceOverrideStatus,
    );
  }) ?? [];

const getAddonsTableRows = (orderData) =>
  orderData?.value?.product_details?.misc_products
    ?.filter(
      (misc) =>
        ![MEDICAL_INSURANCE.id, CLASSES_EDUCATIONAL_FEE.id, SPAIN_MEDICAL_INSURANCE.id].includes(
          misc?.misc_product?.id,
        ),
    )
    ?.map((misc) => {
      const priceOverrideStatus = getPriceOverrideStatusForRow(misc);
      return getTableRowData(
        misc?.misc_product?.id ?? "",
        `Addons: ${orderData?.value?.product_details?.misc_products?.length}`,
        misc?.misc_product?.name ?? "",
        "",
        "",
        `${formattedPriceFromCents(misc?.student_total_price_in_cents)}`,
        `${formattedPriceFromCents(misc?.institution_total_price_in_cents)}`,
        `${formattedPriceFromCents(misc?.total_price_in_cents)}`,
        "",
        misc?.status ?? "",
        misc?.updated_at ?? misc?.created_at ?? "",
        priceOverrideStatus,
      );
    }) ?? [];

const getClassesAcademicFeesRow = (classFeeProducts) => {
  const priceOverrideStatus = getPriceOverrideStatusForRow(classFeeProducts);
  return (
    getTableRowData(
      classFeeProducts?.misc_product?.id ?? "",
      "Academics: Classes",
      { name: "Classes Fee" },
      "",
      "",
      `${formattedPriceFromCents(classFeeProducts?.student_total_price_in_cents)}`,
      `${formattedPriceFromCents(classFeeProducts?.institution_total_price_in_cents)}`,
      `${formattedPriceFromCents(classFeeProducts?.total_price_in_cents)}`,
      "",
      classFeeProducts?.status ?? "",
      classFeeProducts?.updated_at ?? classFeeProducts?.created_at ?? "",
      priceOverrideStatus,
    ) ?? {}
  );
};

const internshipDataMapper = (orderInternshipsData) =>
  orderInternshipsData.value?.data?.data?.items?.reduce((acc, current) => {
    if (current?.id) {
      return {
        ...acc,
        [current?.id]: {
          ...current,
          company: current?.company?.name,
        },
      };
    }
    return { ...acc };
  }, {}) ?? {};

const eventsDatesMapper = (orderEventOccurrencesData) =>
  orderEventOccurrencesData.value?.data?.data?.items?.reduce((acc, current) => {
    if (current?.id) {
      return {
        ...acc,
        [current?.id]: {
          location_city: current?.associated_event?.event_locations?.[0]?.city?.city,
          location_country: current?.associated_event?.event_locations?.[0]?.city?.country?.name,
          event_duration: current?.associated_event?.duration ?? 0,
          event_duration_unit: current?.associated_event?.duration_units ?? "",
          start_date: current?.start_date_time,
          end_date: current?.end_date_time,
        },
      };
    }
    return { ...acc };
  }, {}) ?? {};

export const getTableData = ({
  orderPaymentProcessing,
  orderData,
  housingData,
  classesFeesIndex,
  orderInternshipsData,
  orderEventOccurrencesData,
  orderRoomHousingId,
  programSession,
}) => {
  const classFeeProducts = orderData.value?.product_details?.misc_products[classesFeesIndex?.value];
  const roomObject = orderData.value?.product_details?.rooms?.[0];
  const internship = internshipDataMapper(orderInternshipsData);
  const events = eventsDatesMapper(orderEventOccurrencesData);
  const housing = housingData?.value?.data?.data;
  return [
    getSessionTableRow(orderPaymentProcessing, orderData, programSession),
    getHousingTableRow(housing, roomObject, orderRoomHousingId),
    getInsuranceTableRow(orderData),
    getClassesAcademicFeesRow(classFeeProducts),
    ...getInternshipTableRows(orderData, internship),
    ...getOccurrencesTableRows(orderData, events),
    ...getAddonsTableRows(orderData),
  ];
};
